// #region: Class definition *********************************************************************/
/**
 * @class coreUI
 * @description Manages core UI javascript functionality providing utilities for dialog management, 
 * file type detection, mobile device detection, and safe data sanitization for cross-frame communication.
 * This class serves as a central hub for all UI helper methods that operate independently of jQuery or Kendo.
 *
 * @example
 * // Check if on mobile device
 * const isMobile = coreUI.isMobile();
 * 
 * // Show confirmation dialog
 * coreUI.showConfirmDialog({
 *     contentHtml: '<p>Are you sure you want to delete this item?</p>',
 *     callbackOk: () => console.log('Confirmed'),
 *     callbackCancel: () => console.log('Cancelled')
 * });
 * 
 * // Sanitize data for postMessage
 * const safeData = coreUI.sanitizeForPostMessage(complexObject);
 * window.parent.postMessage(safeData, window.origin);
 *
 * @remarks
 * The manifest for core-ui.js includes/needs:
 *     - core.js (base functionality)
 *     - core-ui.js (this file)
 *     - dialog-window.js (webcomponent for dialogs)
 * 
 * Dependencies:
 *     - core library for base functionality
 *     - dialog-window webcomponent for modal dialogs
 * 
 * Architecture:
 *     - Singleton pattern using IIFE (Immediately Invoked Function Expression)
 *     - Provides utility methods without external framework dependencies
 *     - Handles cross-browser compatibility for UI operations
 *     - Ensures memory safety with proper cleanup in dialog operations
 *
 * @memberof window
 */
window.coreUI = new class {

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {
        // Magic numbers for attachment preview.
        this.DEFAULT_DIALOG_WIDTH = 900;
        this.DEFAULT_DIALOG_HEIGHT = 700;

        // Magic number for the header size.
        this.HEADER_HEIGHT = 41;
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {String} The string representation of this class.
     */
    toString() {
        return "[coreUI]";
    }

    static toString() {
        return "[coreUI]";
    }

    /** 
     * Identifies the version.
     * @returns {String} The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Returns the height for the popup window, either the requested height or the available height.
     * Handles both absolute pixel values and percentage-based heights relative to the viewport.
     * @param {number|string} desiredHeight The preferred window height in pixels or as percentage string (e.g., "80%").
     * @param {Window} [tabWindow=window] The target window object for dimension calculations.
     * @returns {number} The calculated height in pixels, clamped to viewport maximum.
     *
     * @memberof coreUI
     */
    getPopupHeight(desiredHeight, tabWindow) {
        const maxHeight = (tabWindow ?? window).innerHeight;

        if (typeof desiredHeight === "string" && desiredHeight.indexOf("%") > -1) {
            desiredHeight = maxHeight * parseFloat(desiredHeight, 10) / 100;
        }

        const newDesiredHeight = desiredHeight + this.HEADER_HEIGHT;

        return Math.min(maxHeight, newDesiredHeight);
    }

    /**
     * Returns the width for the popup window, either the requested width or the available width.
     * Handles both absolute pixel values and percentage-based widths relative to the viewport.
     * @param {number|string} desiredWidth The preferred window width in pixels or as percentage string (e.g., "90%").
     * @param {Window} [tabWindow=window] The target window object for dimension calculations.
     * @returns {number} The calculated width in pixels, clamped to viewport maximum.
     *
     * @memberof coreUI
     */
    getPopupWidth(desiredWidth, tabWindow) {
        const maxWidth = (tabWindow ?? window).innerWidth;

        if (typeof desiredWidth === "string" && desiredWidth.indexOf("%") > -1) {
            desiredWidth = maxWidth * parseFloat(desiredWidth, 10) / 100;
        }

        return Math.min(maxWidth, desiredWidth);
    }

    /**
     * Checks if an element is interactive (focusable).
     * @param {Element} element The element to check.
     * @returns {boolean} True if the element is interactive.
     * 
     * @memberof coreUI
     */
    isInteractive(element) {
        if (!element || element === document.body) {
            return false;
        }

        // Check if element is focusable.
        const focusableSelectors = [
            '[contenteditable="true"]',
            '[tabindex]:not([tabindex="-1"])',
            'a[href]',
            'area[href]',
            'button',
            'iframe',
            'input',
            'select',
            'textarea'
        ];

        // Check if element matches any focusable selector.
        const isFocusable = focusableSelectors.some((selector) => {
            try {
                return element.matches && element.matches(selector);
            }
            catch (e) {
                return false;
            }
        });

        // Check if element is disabled.
        const isDisabled = element.disabled || element.getAttribute('aria-disabled') === 'true';

        return isFocusable && !isDisabled;
    }

    /**
     * Checks if a file is an image based on its extension.
     * Supports common image formats: jpg, jpeg, png, gif, bmp, webp.
     * @param {string} fileName The filename to check including extension.
     * @returns {boolean} True if the file is an image, false otherwise.
     * @throws {TypeError} When fileName is not a string.
     *
     * @memberof coreUI
     */
    isImage(fileName) {
        if (fileName && fileName.indexOf(".") > -1) {
            const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "heic", "heif"];
            const extension = fileName.split(".").pop().toLowerCase();

            return imageExtensions.indexOf(extension) > -1;
        }

        return false;
    }

    /**
     * Detects if the current device is a mobile device.
     * Uses modern CSS media queries and touch capability detection for accurate mobile detection.
     * @returns {boolean} True if on mobile device, false otherwise.
     *
     * @memberof coreUI
     */
    isMobile = () => window.matchMedia('(hover: none)').matches || 'ontouchstart' in window;

    /**
     * Checks if a file is a PDF based on its extension.
     * @param {string} fileName The filename to check including extension.
     * @returns {boolean} True if the file is a PDF, false otherwise.
     *
     * @memberof coreUI
     */
    isPdf(fileName) {
        if (fileName && fileName.indexOf(".") > -1) {
            const extension = fileName.split(".").pop().toLowerCase();

            return extension === "pdf";
        }

        return false;
    }

    /**
     * Checks if a file can be previewed (image or PDF).
     * Combines image and PDF detection to determine if file supports preview functionality.
     * @param {string} fileName The filename to check including extension.
     * @returns {boolean} True if the file can be previewed, false otherwise.
     *
     * @memberof coreUI
     */
    isPreviewable(fileName) {
        return this.isImage(fileName) || this.isPdf(fileName);
    }

    /**
     * Sanitizes data objects to be safe for transmission through postMessage by removing DOM elements, functions, and other potentially unsafe objects.
     * Creates a deep clone with only serializable data (strings, numbers, booleans, arrays, and plain objects).
     * @param {*} data The data to sanitize - can be any JavaScript value.
     * @param {Set} [visited=new Set()] Internal set to track circular references during recursion.
     * @returns {*} The sanitized data safe for postMessage transmission, or null if data cannot be serialized.
     * @throws {Error} When circular references are detected beyond the internal handling.
     *
     * @remarks
     * This method provides comprehensive sanitization for cross-frame communication:
     * - Removes DOM elements, functions, and browser-specific objects.
     * - Handles circular references to prevent infinite recursion.
     * - Converts Map and Set objects to plain objects/arrays.
     * - Preserves only JSON-serializable data types.
     * - Returns null for unsupported data types rather than throwing errors.
     *
     * @memberof coreUI
     */
    sanitizeForPostMessage(data, visited = new Set()) {
        // Handle null and undefined.
        if (data == null) {
            return data;
        }

        // Prevent circular references.
        if (visited.has(data)) {
            return null;
        }

        // Handle primitive types (string, number, boolean).
        const type = typeof data;

        if (type === 'number' || type === 'boolean' || type === 'string') {
            return data;
        }

        // Handle functions - exclude them.
        if (type === 'function') {
            return null;
        }

        // Handle DOM elements - exclude them.
        if (data instanceof Node || data instanceof Window || data instanceof HTMLElement || data instanceof Element) {
            return null;
        }

        // Handle other browser objects that should not be transmitted.
        if (data instanceof Event || data instanceof Error || data instanceof RegExp) {
            return null;
        }

        // Handle ArrayBuffer, DataView, TypedArrays.
        if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
            return null;
        }

        // Handle Map and Set - convert to plain objects/arrays.
        if (data instanceof Map) {
            const result = {};

            visited.add(data);

            for (const [key, value] of data) {
                if (typeof key === 'string' || typeof key === 'number') {
                    result[key] = this.sanitizeForPostMessage(value, visited);
                }
            }

            visited.delete(data);

            return result;
        }

        if (data instanceof Set) {
            visited.add(data);

            const result = Array.from(data).map(item => this.sanitizeForPostMessage(item, visited));

            visited.delete(data);

            return result;
        }

        // Handle arrays.
        if (Array.isArray(data)) {
            visited.add(data);

            const result = data.map(item => this.sanitizeForPostMessage(item, visited));

            visited.delete(data);

            return result;
        }

        // Handle plain objects.
        if (type === 'object') {
          
            // Explicitly exclude known dangerous object types.
            if (data instanceof Window || 
                data instanceof Document || 
                data instanceof HTMLElement || 
                data instanceof Element || 
                data instanceof Node ||
                data instanceof Event ||
                data instanceof FormData ||
                data instanceof File ||
                data instanceof Blob ||
                data instanceof XMLHttpRequest ||
                data instanceof Response ||
                data instanceof Request) {
                return null;
            }
            
            // Allow objects that can be enumerated and have own properties (most regular objects).
            // This is much more permissive than the previous approach.
            try {
                // Test if we can access the object's properties.
                Object.keys(data);
            }
            catch {
                // If we can't enumerate properties, it's likely a problematic object.
                return null;
            }

            visited.add(data);
            const result = {};

            for (const key in data) {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    result[key] = this.sanitizeForPostMessage(data[key], visited);
                }
            }

            visited.delete(data);

            return result;
        }

        // For any other type, exclude it.
        return null;
    }

    /**
     * Shows a confirmation dialog using the dialog-window webcomponent.
     * Creates a modal dialog with customizable content, buttons, and callbacks for user confirmation workflows.
     * @param {Object} options Configuration object for the dialog.
     * @param {string} options.contentHtml The HTML content to display in the dialog body.
     * @param {Function} options.callbackOk Function to execute when confirmation button is clicked.
     * @param {Function} [options.callbackCancel] Function to execute when cancel button is clicked.
     * @param {string} [options.buttonText] Custom text for the confirm button, defaults to localized "Delete".
     * @param {string} [options.title] Custom dialog title, defaults to localized "Confirm".
     * @param {string} [options.buttonClass='button-danger'] CSS class for the confirm button styling.
     * @returns {HTMLElement} The dialog element instance for further manipulation.
     *
     * @remarks
     * This method provides a standardized confirmation dialog interface:
     * - Uses the dialog-window webcomponent for consistent styling
     * - Automatically handles button event binding and cleanup
     * - Supports internationalization through the resources object
     * - Provides proper memory management with event listener cleanup
     * - Modal dialog behavior with viewport clamping
     * - Responsive sizing based on viewport dimensions
     *
     * @memberof coreUI
     */
    showConfirmDialog({ contentHtml, callbackOk, callbackCancel, buttonText, title, buttonClass = 'button-danger' }) {
        // Set defaults from resources if available.
        const confirmButtonText = buttonText || (resources?.confirmDialogMessages?.delete || 'Delete');
        const dialogTitle = title || (resources?.confirmDialogMessages?.title || 'Confirm');
        const cancelButtonText = resources?.confirmDialogMessages?.cancel || 'Cancel';

        // Determine button icon based on button class.
        const buttonIcon = buttonClass === 'button-danger' ? 'lf-icon-delete' : 'lf-icon-check';

        // Create dialog content structure.
        const dialogContent = document.createElement('div');
        dialogContent.className = 'popup-page';

        // Create content section.
        const popupContent = document.createElement('div');
        popupContent.className = 'popup-content';
        popupContent.innerHTML = contentHtml;

        // Create toolbar section.
        const popupToolbar = document.createElement('div');
        popupToolbar.className = 'popup-toolbar';

        // Create cancel button.
        const cancelButton = document.createElement('button');
        cancelButton.type = 'button';
        cancelButton.className = 'button button-default button-tall';
        cancelButton.innerHTML = `<span class="button-icon lf-icon-undo"></span><span>${cancelButtonText}</span>`;

        // Create confirm button.
        const confirmButton = document.createElement('button');
        confirmButton.type = 'button';
        confirmButton.className = `button button-tall ${buttonClass}`;
        confirmButton.innerHTML = `<span class="button-icon ${buttonIcon}"></span><span>${confirmButtonText}</span>`;

        // Assemble toolbar.
        popupToolbar.appendChild(cancelButton);
        popupToolbar.appendChild(confirmButton);

        // Assemble dialog content.
        dialogContent.appendChild(popupContent);
        dialogContent.appendChild(popupToolbar);

        // Create dialog-window element.
        const dialogElement = document.createElement('dialog-window');

        // Add event handlers.
        const handleConfirm = () => {
            dialogElement.close();

            if (typeof callbackOk === 'function') {
                callbackOk();
            }
        };

        const handleCancel = () => {
            dialogElement.close();

            if (typeof callbackCancel === 'function') {
                callbackCancel();
            }
        };

        const handleClose = () => {
            // Clean up event listeners.
            confirmButton.removeEventListener('click', handleConfirm);
            cancelButton.removeEventListener('click', handleCancel);
            dialogElement.removeEventListener('dialogclose', handleClose);
            
            // Remove dialog element from DOM.
            if (dialogElement.parentNode) {
                dialogElement.parentNode.removeChild(dialogElement);
            }
        };

        // Attach event listeners.
        confirmButton.addEventListener('click', handleConfirm);
        cancelButton.addEventListener('click', handleCancel);
        dialogElement.addEventListener('dialogclose', handleClose);

        // Add to DOM.
        document.body.appendChild(dialogElement);

        // Open the dialog.
        dialogElement.open(dialogContent, {
            title: dialogTitle,
            width: this.getPopupWidth(400),
            height: this.getPopupHeight(200),
            modal: true,
            clampToViewport: true
        });

        return dialogElement;
    }
    // #endregion ********************************************************************************/
    // 
}();
// #endregion ************************************************************************************/